import {
  RowSegment,
  Segment,
  SegmentArray,
  Shape,
  TextSegment,
} from 'ui/workflows/flowchart/types';
import { TextAlign } from '../../constants';
import { truncateTextIfNeeded, updateUnderline } from '../textUtils';
import { addTooltipToText } from '../tooltipUtils';
import { drawSegment } from './segment';
import { DrawSegmentReturn, DrawSegmentsArgs, DrawSegmentsReturn } from './types';

/**
 * Center all the text in a row
 */
const centerLine = ({
  shapeDetails,
  startX = 0,
  endX = 0,
}: {
  /** Line details after drawing */
  shapeDetails: DrawSegmentsReturn;
  /** Start position of the row */
  startX: number;
  /** End position of the row */
  endX: number;
}) => {
  const { shapes = [], width = 0 } = shapeDetails;
  // The center point of the row
  const rowCenter = startX + (endX - startX) / 2;
  // Where the segments in the row should start
  let x = rowCenter - width / 2;

  shapes.forEach((shape) => {
    // NB: Text needs to be aligned to `TextAlign.Start` as many other
    // calculations (EG: the position of the underline) are dependent on this
    // assumption.
    shape.attr('textAlign', TextAlign.Start);
    // Move the text to the correct position
    shape.attr('x', x);
    // If the text is underlined, move that underline to the correct position
    if (shape.get('underline')) {
      updateUnderline({ shape });
    }
    x += shape.getBBox().width;
  });
};

/** Type narrowing for `SegmentArray` */
const isSegmentArray = (segments: Segment | SegmentArray): segments is Segment[] =>
  Array.isArray(segments);

/** Type narrowing for `RowSegment` */
const isSegmentRow = (segment: Segment): segment is RowSegment => segment?.isRow === true;

type SegmentDetails = Omit<DrawSegmentReturn, 'shape'> & { shape: Shape | Shape[] | null };

/**
 * This draws different parts of the text, one at a time.
 * It allows you to put different tooltips on specific parts of the text
 * without making a different function call for each node time.
 */
export const drawSegments = (opts: DrawSegmentsArgs): DrawSegmentsReturn => {
  const {
    className,
    group,
    isTruncated = true,
    maxWidth = Infinity,
    segments = [],
    showTooltipIfTruncated = true,
    startX,
    startY,
    style,
    rowNumber = 0,
  } = opts || {};
  const truncationIsNeeded = isTruncated && Number.isFinite(maxWidth);
  let segmentIsComplete = false;
  let width = 0;
  let height = 0;
  let currentX = startX;
  let currentY = startY || style?.y || 0;
  let localRowNumber = rowNumber;

  // For testing purposes
  group.set('maxWidth', maxWidth);

  const shapes = segments.reduce((acc: Shape[], segment, i) => {
    if (segmentIsComplete) return acc;

    const startWidth = width;
    const name = `${className}-${i}`;
    const segmentIsArray = isSegmentArray(segment);
    const segmentIsRow = !segmentIsArray && isSegmentRow(segment);
    const lineIsNew = segmentIsArray || segmentIsRow;
    let segmentDetails: SegmentDetails = { shape: null, width: 0, height: 0 };

    if (lineIsNew) {
      const details = drawSegments({
        ...opts,
        startX: currentX,
        startY: currentY,
        segments: segmentIsArray ? segment : segment?.segments || [],
        className,
        rowNumber: localRowNumber,
      });
      if (segmentIsRow && segment?.style?.textAlign === TextAlign.Center) {
        centerLine({
          shapeDetails: details,
          startX: currentX,
          endX: startX + maxWidth,
        });
      }
      segmentDetails = { ...details, shape: details.shapes };
      currentY += segmentDetails.height;
      localRowNumber += 1;
    } else {
      segmentDetails = drawSegment({
        group,
        maxWidth: maxWidth - startWidth,
        name,
        segment,
        style,
        x: currentX,
        y: currentY,
        rowNumber: localRowNumber,
      });
      currentX += segmentDetails.width;
      width += segmentDetails.width;
    }

    const { shape, height: shapeHeight } = segmentDetails;

    if (!shape) return acc;

    height = Math.max(height, shapeHeight);

    if (Array.isArray(shape)) {
      return [...acc, ...shape];
    }

    // truncate if you have reached the end of the text
    if (!lineIsNew && truncationIsNeeded && width > maxWidth) {
      truncateTextIfNeeded(shape, { maxWidth: maxWidth - startWidth, maxRows: 1 });

      const { text } = segment as TextSegment;

      if (showTooltipIfTruncated && text) {
        addTooltipToText({
          shape,
          group,
          tooltipText: text,
          maxWidth,
          isUnderlined: false,
        });
      }
      segmentIsComplete = true;
    }
    return [...acc, shape];
  }, []);

  return { shapes, height, width };
};
